import Extension from "./base/extension.js";
import Logger from "./utilities/logger.js";

/**
 * Stencil
 *
 * The global runtime for the theme.
 *
 * Responsibilities (high level):
 * - Hold shared state (Settings, Cache, Logger)
 * - Maintain registries for Extensions and Helpers
 * - Provide lifecycle hooks: PreInitCallbacks, PostInitCallbacks, Extension init/onReady
 * - Provide lazy-loading infrastructure for vendor libraries and heavyweight extensions
 * - Provide small utility statics used across elements, sections and extensions
 *
 * Notes on bundling:
 * - "Vendors" are 3rd-party libraries (Swiper, PhotoSwipe, AOS, TomSelect, etc.) that we
 *   lazy-load via `registerVendor` + `ensureVendor`.
 * - "Extensions" are Stencil classes (Cart, Facets, PropositionsManager, etc.).
 *   Most are eagerly registered from theme.js at startup, but heavyweight / contextual ones
 *   are lazy-loaded via `registerExtensionLoader` + `ensureExtension`.
 *
 * theme.js is responsible for:
 * - Creating `window.Stencil`
 * - Registering vendor loaders and extension loaders
 * - Eagerly registering core extensions
 * - Scanning + lazy-defining custom elements
 * - Calling `Stencil.init()`
 */
class Stencil {
  /**
   * Used in console output. Historically styled for terminal output.
   * (Some logs also use CSS-based %c formatting in theme.js for better browser output.)
   */
  logPrefix = "\x1b[30;45;1m[Stencil]";

  /**
   * Global runtime flags.
   * debug is enabled via querystring `?mpd=1` or via `window.theme.isDevelopment`.
   */
  Settings = { debug: false };

  /**
   * Lifecycle callback queues
   *
   * - PreInitCallbacks:
   *   Intended for "setup" tasks that must run before extensions init.
   *
   * - PostInitCallbacks:
   *   Intended for tasks that can run after extensions init (and after vendors/elements are ready).
   *
   * Both queues support sync and async callbacks (Promises are awaited).
   *
   * Important:
   * - These queues are drained once during `init()`.
   * - If you push callbacks after `init()` has completed, they will NOT run unless you
   *   explicitly run them again (or implement an auto-drain push hook).
   */
  PreInitCallbacks = [];
  PostInitCallbacks = [];

  /**
   * Registered extensions live here by name.
   * e.g. `Stencil.Extensions.Cart`, `Stencil.Extensions.PropositionsManager`
   */
  Extensions = {};

  /**
   * Helper functions registered by extensions for other extensions to call.
   * Exposed as callables with a fixed `this` binding (via registerHelper).
   */
  Helpers = {};

  /**
   * General-purpose cache for any runtime values that should persist for the page session.
   */
  Cache = new Map();

  /**
   * Project logger utility (custom).
   */
  Logger = new Logger();

  /**
   * Vendor lazy-loader registries
   *
   * VendorLoaders:   map of name -> async loader function
   * VendorPromises:  map of name -> in-flight/resolved Promise
   *
   * This is how we avoid shipping heavy vendor code globally, and only load it when a
   * feature actually needs it.
   */
  VendorLoaders = {};
  VendorPromises = {};

  /**
   * Extension lazy-loader registries
   *
   * ExtensionLoaders:  map of name -> async loader function (dynamic import)
   * ExtensionPromises: map of name -> in-flight/resolved Promise
   *
   * Used for heavyweight/contextual extensions that we do not want in the main bundle.
   */
  ExtensionLoaders = {};
  ExtensionPromises = {};

  /**
   * Used to indicate Stencil has been constructed on this page.
   * (Also used for migration / state transfer via Object.assign from an existing window.Stencil.)
   */
  hasLoaded = false;

  constructor() {
    /**
     * Migration/rehydration support:
     * If a previous `window.Stencil` exists (e.g. script reloaded, hot reload, etc.),
     * copy its state into this instance.
     *
     * This is also why most properties are declared as fields above: Object.assign will
     * overwrite them if earlier state exists.
     */
    // @ts-ignore
    Object.assign(this, window.Stencil);

    this.hasLoaded = true;

    /**
     * Debug flag:
     * - `?mpd=1` enables debug mode
     * - `window.theme.isDevelopment` also enables debug mode when present
     */
    const urlParams = new URLSearchParams(window.location.search);
    // @ts-ignore
    this.Settings.debug = urlParams.get("mpd") || window.theme?.isDevelopment;

    /**
     * Bootstrap Alerts:
     * Any `.alert` elements already in the DOM are initialised here.
     *
     * Note:
     * - This assumes `bootstrap` is globally available (from vendor.js).
     * - If we ever lazy-load Bootstrap, this needs to move behind `ensureVendor("bootstrap")`
     *   or be handled by a small extension.
     */
    let alertList = document.querySelectorAll(".alert");
    alertList.forEach(function (alert) {
      // @ts-ignore
      new bootstrap.Alert(alert);
    });

    /**
     * Extension onReady hook:
     * Runs after DOMContentLoaded for any extension that implements `onReady()`.
     *
     * This is useful for extensions that need the DOM to exist (selectors, listeners),
     * but should not block initial init().
     */
    document.addEventListener("DOMContentLoaded", () => {
      // @ts-ignore
      for (const [key, value] of Object.entries(this.Extensions)) {
        if ("onReady" in value) {
          try {
            value.onReady();
          } catch (e) {
            console.error(e);
          }
        }
      }
    });

    /**
     * Smooth-scroll for in-page anchors within #MainContent.
     * Excludes links marked with [data-hash-ignore].
     */
    document
      .querySelectorAll("#MainContent a[href*='#']:not([data-hash-ignore])")
      .forEach((element) => {
        if (!(element instanceof HTMLAnchorElement)) return;
        element.addEventListener("click", (e) => {
          e.preventDefault();

          // Prefer element.hash; fallback to parsing the full href.
          const hash = element.hash || new URL(element.href).hash;
          if (!hash) return;

          const target = document.querySelector(hash);
          if (target) {
            target.scrollIntoView({ behavior: "smooth" });
          }
        });
      });

    /**
     * Dismissable UI blocks:
     * - Elements with [data-dismiss] toggle visibility of a selector stored in data-dismiss.
     * - When dismissed, a cookie is stored for 7 days to keep it hidden.
     *
     * Note:
     * - Assumes Cookies.js is globally available (from vendor.js) as `Cookies`.
     * - In Shopify theme editor (designMode), we ignore cookie state so editors can see blocks.
     */
    document.querySelectorAll("[data-dismiss]").forEach((element) => {
      // @ts-ignore
      if (!Cookies.get(`dismiss-${element.dataset.dismiss}`) || Shopify.designMode) {
        // @ts-ignore
        document.querySelector(element.dataset.dismiss)?.classList.remove("d-none");
      }

      // @ts-ignore
      element.addEventListener("click", (e) => {
        // @ts-ignore
        document.querySelector(element.dataset.dismiss)?.remove();
        // @ts-ignore
        Cookies.set(`dismiss-${element.dataset.dismiss}`, true, { expires: 7 });
      });
    });

    /**
     * Shopify theme editor integration:
     * When a section reloads in the editor, ensure dismissable elements are visible.
     */
    // @ts-ignore
    if (Shopify.designMode) {
      document.addEventListener("shopify:section:load", (e) => {
        // @ts-ignore
        e.target.querySelectorAll("[data-dismiss]").forEach((element) => {
          document.querySelector(element.dataset.dismiss)?.classList.remove("d-none");
          console.log("[MP] Unhide element with data-dismiss", element.dataset.dismiss);
        });
      });
    }
  }

  /**
   * init()
   *
   * Runs the primary Stencil lifecycle.
   *
   * Order:
   * 1) Drain PreInitCallbacks (awaiting async)
   * 2) Call init() on all registered extensions (best effort, isolated try/catch)
   * 3) Drain PostInitCallbacks (awaiting async)
   *
   * Notes:
   * - Extensions are registered by theme.js (eager) and/or by ensureExtension (lazy).
   * - If an extension is lazy-loaded after init, its init() will not run automatically
   *   unless the caller triggers it. (That may be fine depending on usage.)
   */
  async init() {
    await this.runPreInitCallbacks();

    // @ts-ignore
    for (const [key, value] of Object.entries(this.Extensions)) {
      if ("init" in value) {
        try {
          value.init();
        } catch (e) {
          console.error(e);
        }
      }
    }

    await this.runPostInitCallbacks();
  }

  /**
   * Returns true if an extension loader has been registered for the given name.
   * Used by theme.js to avoid eager-bundling extensions that should be lazy.
   */
  hasExtensionLoader(name) {
    return Object.keys(this.ExtensionLoaders).includes(name);
  }

  /**
   * Register an extension instance.
   * Extensions are stored by `extension.name` in `this.Extensions`.
   *
   * @param {Extension} extension
   */
  registerExtension(extension) {
    if (Object.keys(this.Extensions).includes(extension.name)) {
      throw new Error(`Extension with name '${extension.name}' already exists`);
    }
    this.Extensions[extension.name] = extension;
  }

  /**
   * Register a helper function for cross-extension use.
   * The function is bound to `context` and stored by name.
   *
   * @param {String} name
   * @param {Function} func
   * @param {Object} context
   */
  registerHelper(name, func, context) {
    if (Object.keys(this.Helpers).includes(name)) {
      throw new Error(`Helper with name '${name}' already exists`);
    }
    this.Helpers[name] = func.bind(context);

    // Broadcast helper registration so listeners can react.
    const event = new CustomEvent("helper:registered", { detail: { name: name } });
    document.dispatchEvent(event);
  }

  /**
   * Drain PreInitCallbacks queue.
   * Supports both sync and async callbacks.
   */
  async runPreInitCallbacks() {
    while (this.PreInitCallbacks.length) {
      const cb = this.PreInitCallbacks.shift();

      try {
        const result = cb.call();
        if (result && typeof result.then === "function") {
          await result;
        }
      } catch (e) {
        console.error(this.logPrefix, "Failed to run pre-init callback with error", e);
      }
    }
  }

  /**
   * Drain PostInitCallbacks queue.
   * Supports both sync and async callbacks.
   */
  async runPostInitCallbacks() {
    while (this.PostInitCallbacks.length) {
      const cb = this.PostInitCallbacks.shift();

      try {
        const result = cb.call();
        if (result && typeof result.then === "function") {
          await result;
        }
      } catch (e) {
        console.error(this.logPrefix, "Failed to run post-init callback with error", e);
      }
    }
  }

  /**
   * Register a vendor loader (lazy import hook).
   *
   * @param {String} name
   * @param {Function} loader async function that returns the vendor module/handle
   */
  registerVendor(name, loader) {
    if (this.VendorLoaders[name]) {
      throw new Error(`Vendor loader '${name}' already exists`);
    }
    this.VendorLoaders[name] = loader;
  }

  /**
   * Ensure vendor is loaded exactly once.
   * Returns a cached Promise so concurrent calls share the same request.
   *
   * @param {String} name
   * @returns {Promise<any>}
   */
  async ensureVendor(name) {
    const loader = this.VendorLoaders[name];
    if (!loader) throw new Error(`No vendor loader registered for '${name}'`);

    if (!this.VendorPromises[name]) {
      this.VendorPromises[name] = loader();
    }

    return this.VendorPromises[name];
  }

  /**
   * Ensure a stylesheet is present in <head>.
   * Returns a Promise that resolves when the CSS has loaded.
   *
   * @param {string} href The CSS URL
   * @param {string} id A unique id for the <link> element
   */
  ensureStylesheet(href, id) {
    if (!href) return Promise.resolve();

    const existing = document.getElementById(id);
    if (existing) {
      // If it already loaded, we're done
      if (existing.dataset.loaded === "true") return Promise.resolve();

      // Otherwise wait for its load event
      return new Promise((resolve, reject) => {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error(`Failed to load CSS: ${href}`)), { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = href;

      link.addEventListener(
        "load",
        () => {
          link.dataset.loaded = "true";
          resolve();
        },
        { once: true }
      );

      link.addEventListener(
        "error",
        () => reject(new Error(`Failed to load CSS: ${href}`)),
        { once: true }
      );

      document.head.appendChild(link);
    });
  }


  /**
   * Register an extension loader (lazy import hook).
   *
   * The loader should typically be a dynamic import() returning a module with a default export
   * (the extension class).
   *
   * @param {String} name
   * @param {Function} loader async function that returns the extension module
   */
  registerExtensionLoader(name, loader) {
    if (this.ExtensionLoaders[name]) {
      throw new Error(`Extension loader '${name}' already exists`);
    }
    this.ExtensionLoaders[name] = loader;
  }

  /**
   * Ensure an extension is available exactly once.
   *
   * - If already registered, returns it immediately.
   * - Otherwise, uses the registered loader to import the module, constructs it, registers it,
   *   then returns the instance.
   *
   * This enables lazy-loading heavyweight/contextual extensions without bloating the main bundle.
   *
   * @param {String} name Extension name (must match loader name and the extension's expected name)
   * @returns {Promise<Extension>}
   */
  async ensureExtension(name) {
    // Already registered?
    if (this.Extensions?.[name]) return this.Extensions[name];

    // In-flight / cached?
    if (!this.ExtensionPromises[name]) {
      const loader = this.ExtensionLoaders[name];
      if (!loader) throw new Error(`No extension loader registered for '${name}'`);

      this.ExtensionPromises[name] = (async () => {
        const mod = await loader();
        const ExtClass = mod.default;

        // We pass the name through so the extension instance matches registry keying.
        const instance = new ExtClass(name);

        this.registerExtension(instance);
        return instance;
      })();
    }

    return this.ExtensionPromises[name];
  }

  /**
   * Template helpers used in various parts of the theme.
   * These rely on body/template classes you add on Shopify templates.
   */
  static isProductPage() {
    return document.querySelectorAll(".template-product").length > 0;
  }

  static isCollectionPage() {
    return document.querySelectorAll(".template-collection").length > 0;
  }

  static isCartPage() {
    return document.querySelectorAll(".template-cart").length > 0;
  }

  static isBlogPage() {
    return document.querySelectorAll(".template-blog").length > 0;
  }

  /**
   * Shopify-style money formatter.
   * `cents` can be a string or number.
   */
  static formatMoney(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    let value = "";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format;

    function defaultOption(opt, def) {
      return typeof opt === "undefined" ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ",");
      decimal = defaultOption(decimal, ".");

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split(".");
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      const cents = parts[1] ? decimal + parts[1] : "";

      return dollars + cents;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  /**
   * Convert text into a Shopify-ish handle.
   */
  static handleise(text) {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w\-]+/g, "")
      .replace(/\-\-+/g, "-")
      .replace(/^-+/, "")
      .replace(/-+$/, "");
  }

  static toTitleCase(string) {
    return string[0].toUpperCase() + string.slice(1).toLowerCase();
  }

  /**
   * Read a CSS custom property from :root using the `--st-` prefix.
   */
  static getStencilVariable(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(`--st-${name}`);
  }

  /**
   * Dump all `--st-*` CSS custom properties into an object.
   * Mainly useful for debugging.
   */
  static getAllStencilVariables() {
    const prefix = "--st-";
    const styles = getComputedStyle(document.documentElement);
    const variables = {};

    for (let i = 0; i < styles.length; i++) {
      const name = styles[i];
      if (name.startsWith(prefix)) {
        const key = name.slice(prefix.length);
        variables[key] = styles.getPropertyValue(name).trim();
      }
    }

    return variables;
  }

  /**
   * Swiper helpers
   *
   * These utility methods convert Bootstrap column classes and gutter classes into Swiper
   * breakpoints. They are used by the <swiper-slider> custom element and elsewhere.
   */

  static getSwiperBreakpointsByBSColClass(columnClass) {
    const itemsPerRow = this.colClassToItemsPerRow(columnClass);
    const bsBreakpoints = this.getBSBreakpoints();

    let breakpoints = { 0: { slidesPerView: itemsPerRow.xs } };
    for (const [key, value] of Object.entries(itemsPerRow)) {
      if (key !== "xs") {
        breakpoints[bsBreakpoints[key]] = { slidesPerView: value };
      }
    }
    return breakpoints;
  }

  static getBSBreakpoints() {
    const rootStyles = getComputedStyle(document.documentElement);
    return ["sm", "md", "lg", "xl", "xxl", "xxxl"].reduce(
      (breakpoints, bp) => {
        breakpoints[bp] = parseInt(
          rootStyles.getPropertyValue(`--bs-breakpoint-${bp}`).trim(),
          10
        );
        return breakpoints;
      },
      { xs: 0 }
    );
  }

  /**
   * Convert Bootstrap column classes to items-per-row per breakpoint.
   * Supports:
   * - col-6, col-lg-3
   * - col-auto, col-md-auto
   * - custom classes like colx-⅒, colx-lg-⅛, etc.
   */
  static colClassToItemsPerRow(classString = "") {
    const breakpoints = ["xs", "sm", "md", "lg", "xl", "xxl", "xxxl"];
    const defaultColumns = 12;

    const customClasses = { "⅒": 10, "⅛": 8, "⅕": 5, "3_10": 3.33 };

    /** @type {Record<string, number|'auto'>} */
    const itemsPerRow = Object.fromEntries(breakpoints.map((bp) => [bp, 1]));

    const classes = classString.trim().split(/\s+/).filter(Boolean);

    classes.forEach((cls) => {
      const mCol = cls.match(/^col(?:-([a-z]{2,3}))?-(\d+|auto)$/);
      if (mCol) {
        const bp = mCol[1] || "xs";
        const spec = mCol[2];

        if (spec === "auto") {
          itemsPerRow[bp] = "auto";
        } else {
          const size = parseInt(spec, 10);
          if (size > 0) itemsPerRow[bp] = defaultColumns / size;
        }
        return;
      }

      if (cls === "col") {
        itemsPerRow["xs"] = "auto";
        return;
      }

      const mColx = cls.match(/^colx(?:-([a-z]{2,3}))?-(.+)$/);
      if (mColx) {
        const bp = mColx[1] || "xs";
        const key = mColx[2];
        if (customClasses[key] != null) {
          itemsPerRow[bp] = customClasses[key];
        }
      }
    });

    // Bootstrap-style mobile-first inheritance
    for (let i = 1; i < breakpoints.length; i++) {
      const prevBp = breakpoints[i - 1];
      const currentBp = breakpoints[i];

      const hasExplicit = classes.some((c) =>
        new RegExp(`-(?:${currentBp})(?:-|$)`).test(c)
      );

      if (!hasExplicit) {
        itemsPerRow[currentBp] = itemsPerRow[prevBp];
      }
    }

    return itemsPerRow;
  }

  static remToPx(rem) {
    // @ts-ignore
    rem = parseFloat(rem);
    return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
  }

  static gutterClassToSpaceBetweenPx(gutterClass) {
    const bps = ["xs", "sm", "md", "lg", "xl", "xxl", "xxxl"];
    const out = Object.fromEntries(bps.map((bp) => [bp, undefined]));

    if (!gutterClass) {
      out.xs = 0;
      for (let i = 1; i < bps.length; i++) {
        out[bps[i]] = out[bps[i - 1]];
      }
      return out;
    }

    const tokens = gutterClass.trim().split(/\s+/);
    const re = /^g(?:-([a-z]{2,3}))?-(\d+)$/;

    tokens.forEach((tok) => {
      const m = re.exec(tok);
      if (!m) return;

      const bp = m[1] || "xs";
      const size = m[2];
      const rem = this.getStencilVariable(`g-${size}`);
      const px = this.remToPx(rem);

      out[bp] = px;
    });

    // Mobile-first inheritance
    for (let i = 1; i < bps.length; i++) {
      if (out[bps[i]] == null) {
        out[bps[i]] = out[bps[i - 1]];
      }
    }

    // Fallback if xs still unset
    if (out.xs == null) {
      out.xs = 0;
      for (let i = 1; i < bps.length; i++) {
        if (out[bps[i]] == null) {
          out[bps[i]] = out[bps[i - 1]];
        }
      }
    }

    return out;
  }

  static getSwiperBreakpointsByColAndGutter(colClass, gutterClass) {
    const itemsPerRow = this.colClassToItemsPerRow(colClass || "");
    const spacingPx = this.gutterClassToSpaceBetweenPx(gutterClass || "");
    const bsBreakpoints = this.getBSBreakpoints();

    const order = ["xs", "sm", "md", "lg", "xl", "xxl", "xxxl"];
    const out = {};

    order.forEach((bp) => {
      const key = bp === "xs" ? 0 : bsBreakpoints[bp];
      out[key] = {
        slidesPerView: itemsPerRow[bp] ?? 1,
        spaceBetween: spacingPx[bp] ?? 0,
      };
    });

    return out;
  }

  /**
   * UI helpers that wrap Bootstrap components.
   * These assume bootstrap is globally available.
   */

  static alert(
    type = "primary",
    content = "<strong>This</strong> is an alert",
    icon = "",
    closeAfter = 5000
  ) {
    const alert = document.querySelector("#alert-container > .alert");
    if (!(alert instanceof HTMLElement)) return;
    alert.className = `${alert.className.replace(/\balert-.+/g, "")} alert-${type}`;

    const alertContent = alert.querySelector(".alert-message");
    if (!(alertContent instanceof HTMLElement)) return;
    let next_content = `<div>${content}</div>`;
    if (icon) {
      next_content = `<i class="fs-4 me-2 ${icon}"></i>${next_content}`;
    }

    alertContent.innerHTML = next_content;
    alert.classList.add("show");

    setTimeout(() => {
      alert.classList.remove("show");
    }, closeAfter);
  }

  static toast(type = "success", content = "<strong>This</strong> is a toast!", icon = "") {
    const container = document.querySelector("#toasts");
    const template = document.querySelector("#toast");
    if (!(container instanceof HTMLElement)) return;
    if (!(template instanceof HTMLTemplateElement)) return;
    const template_first_child = template.content.firstElementChild;
    if (!(template_first_child instanceof HTMLElement)) return;
    const clone = template_first_child.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return;

    const wrapper = clone.querySelector("div:first-child");
    if (!(wrapper instanceof HTMLElement)) return;
    wrapper.className = `${wrapper.className} p-0 m-0 alert alert-${type}`;

    const toast_message = clone.querySelector(".toast-message");
    if (!(toast_message instanceof HTMLElement)) return;
    toast_message.innerHTML = content;

    if (icon) {
      const toast_icon = clone.querySelector(".toast-icon");
      if (toast_icon instanceof HTMLElement) {
        toast_icon.innerHTML = `<i class="fs-4 me-2 ${icon}"></i>`;
      }
    }

    container.appendChild(clone);

    // @ts-ignore
    const toast = new bootstrap.Toast(clone);
    toast.show();
  }

  static fetchConfig(type = "json") {
    return {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: `application/${type}` },
    };
  }
}

export default Stencil;